import { createClient } from '@supabase/supabase-js';
import { logger } from './logger';
import { requiredEnv } from './requiredEnv';

/**
 * Supabase Client Configuration
 * For Velvynote V2 Architecture
 */

const SUPABASE_URL = requiredEnv('REACT_APP_SUPABASE_URL');
const SUPABASE_ANON_KEY = requiredEnv('REACT_APP_SUPABASE_PUBLISHABLE_KEY');
const BACKEND_URL = requiredEnv('REACT_APP_BACKEND_URL');

// Create Supabase client
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Cache for viewport queries
class MemoryCache {
  constructor(maxAge = 10 * 60 * 1000) {
    this.cache = new Map();
    this.maxAge = maxAge;
  }

  getCacheKey(bounds, zoom) {
    // Round bounds to reduce cache misses on small movements
    const precision = zoom < 8 ? 1 : 2;
    return `${bounds.sw.lat.toFixed(precision)},${bounds.sw.lng.toFixed(precision)},` +
      `${bounds.ne.lat.toFixed(precision)},${bounds.ne.lng.toFixed(precision)},${zoom}`;
  }

  get(bounds, zoom) {
    const key = this.getCacheKey(bounds, zoom);
    const entry = this.cache.get(key);
    if (entry && Date.now() - entry.timestamp < this.maxAge) {
      return entry.data;
    }
    return null;
  }

  set(bounds, zoom, data) {
    const key = this.getCacheKey(bounds, zoom);
    this.cache.set(key, { data, timestamp: Date.now() });

    // Prune old entries if cache gets too large
    if (this.cache.size > 100) {
      const oldest = [...this.cache.entries()]
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, 50);
      oldest.forEach(([k]) => this.cache.delete(k));
    }
  }

  clear() {
    this.cache.clear();
  }
}

export const memoryCache = new MemoryCache();

/**
 * Get memories for a viewport
 * Returns GeoJSON FeatureCollection with individual memories
 * MapLibre handles clustering client-side
 */
export async function getMemoriesForViewport(bounds) {
  // Check cache first (use zoom 0 as placeholder since we don't use it anymore)
  const cached = memoryCache.get(bounds, 0);
  if (cached) {
    logger.debug('📦 Using cached memories');
    return cached;
  }

  try {
    const { data, error } = await supabase.rpc('get_memories_for_viewport', {
      sw_lat: bounds.sw.lat,
      sw_lng: bounds.sw.lng,
      ne_lat: bounds.ne.lat,
      ne_lng: bounds.ne.lng,
    });

    if (error) {
      logger.error('Supabase RPC error:', error);
      throw error;
    }

    // Cache the result
    memoryCache.set(bounds, 0, data);

    return data;
  } catch (err) {
    logger.error('Failed to fetch memories:', err);
    // Return empty FeatureCollection on error
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }
}

/**
 * Create a new memory (via backend for content moderation)
 */
export async function createMemoryV2(text, lat, lng, color = '#E6B3C2', userId = null, isAnonymous = true, user = null) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (user) {
      const idToken = await user.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${BACKEND_URL}/memories/create`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text: text.trim().substring(0, 280),
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        color,
        isAnonymous,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to create memory: ${response.status}`);
    }

    // Clear cache so new memory appears
    memoryCache.clear();

    return data;
  } catch (err) {
    logger.error('Failed to create memory:', err);
    throw err;
  }
}

/**
 * Delete a memory (via backend)
 * @param {string} memoryId - Memory UUID
 * @param {Object} user - Firebase user object (for auth token)
 */
export async function deleteMemoryV2(memoryId, user) {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (user) {
      const idToken = await user.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const response = await fetch(`${BACKEND_URL}/memories/${memoryId}`, {
      method: 'DELETE',
      headers,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `Failed to delete memory: ${response.status}`);
    }

    // Clear cache
    memoryCache.clear();

    return data;
  } catch (err) {
    logger.error('Failed to delete memory:', err);
    throw err;
  }
}

/**
 * Get a single memory by ID
 * Used for share page feature
 */
export async function getMemoryById(memoryId) {
  try {
    const { data, error } = await supabase.rpc('get_memory_by_id', {
      p_memory_id: memoryId,
    });

    if (error) {
      logger.error('Supabase RPC error:', error);
      return { success: false, error: error.message };
    }

    return data;
  } catch (err) {
    logger.error('Failed to fetch memory:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get all memories for the authenticated user (via backend)
 * Used for video generation feature and profile
 * @param {Object} user - Firebase user object (for auth token)
 */
export async function getUserMemories(user) {
  try {
    const idToken = await user.getIdToken();

    const response = await fetch(`${BACKEND_URL}/memories/mine`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to fetch memories: ${response.status}`);
    }

    return await response.json();
  } catch (err) {
    logger.error('Failed to fetch user memories:', err);
    throw err;
  }
}


/**
 * Create a memory in a landmark's premium zone
 * This calls the backend which handles gem deduction atomically
 * @param {Object} user - Firebase user object
 * @param {string} text - Memory text
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {string} color - Memory color
 * @param {string} landmarkId - Landmark ID
 * @param {number} gemCost - Gem cost for this landmark
 * @returns {Promise<{ success: boolean, memory?: Object, error?: string }>}
 */
export async function createLandmarkMemory(user, text, lat, lng, color, landmarkId, gemCost) {
  if (!user) {
    return { success: false, error: 'User not authenticated' };
  }

  try {
    const idToken = await user.getIdToken();

    const response = await fetch(`${BACKEND_URL}/memories/create-landmark`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        text: text.trim().substring(0, 280),
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        color: color || '#E6B3C2',
        landmarkId,
        gemCost,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || 'Failed to create landmark memory',
      };
    }

    // Clear cache so new memory appears
    memoryCache.clear();

    return {
      success: true,
      memory: result.memory,
      newBalance: result.new_credit_balance,
    };
  } catch (err) {
    logger.error('Failed to create landmark memory:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Get user profile (auto-creates with random handle if not exists)
 */
export async function getUserProfile(user) {
  try {
    if (!user) {
      return { success: false, error: 'Not authenticated' };
    }

    const idToken = await user.getIdToken();
    const response = await fetch(`${BACKEND_URL}/profile`, {
      headers: { 'Authorization': `Bearer ${idToken}` },
    });

    if (!response.ok) {
      logger.error('Failed to fetch profile:', response.status);
      return { success: false, error: 'Failed to fetch profile' };
    }

    return await response.json();
  } catch (err) {
    logger.error('Failed to fetch user profile:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Update user profile (via backend)
 * @param {Object} user - Firebase user object (for auth token)
 * @param {Object} updates - Profile fields to update
 */
export async function updateUserProfile(user, updates) {
  try {
    const idToken = await user.getIdToken();

    const response = await fetch(`${BACKEND_URL}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        handle: updates.handle || null,
        bio: updates.bio || null,
        country: updates.country || null,
        zodiacSign: updates.zodiacSign || null,
        languages: updates.languages || null,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || `Failed to update profile: ${response.status}` };
    }

    return data;
  } catch (err) {
    logger.error('Failed to update user profile:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Check if a handle is available
 */
export async function checkHandleAvailable(handle, userId = null) {
  try {
    const { data, error } = await supabase.rpc('check_handle_available', {
      p_handle: handle,
      p_user_id: userId,
    });

    if (error) {
      logger.error('Supabase RPC error:', error);
      return { available: false, valid: false, error: error.message };
    }

    return data;
  } catch (err) {
    logger.error('Failed to check handle availability:', err);
    return { available: false, valid: false, error: err.message };
  }
}

export default supabase;
